import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Heart, Share2, Instagram, Facebook, MessageCircle, UserPlus, ExternalLink } from "lucide-react";
import { fetchWhatsapp } from "../lib/api";
import { shareContent } from "../lib/dailyJourney";

// External presence, kept simple, hidden when missing.
const SOCIAL_LINKS = {
  // Public socials of @ccarlosmedeiross
  instagram: "https://instagram.com/ccarlosmedeiross",
  facebook: "https://facebook.com/ccarlosmedeiross",
};

const normalizeWa = (v) => (v || "").replace(/\D/g, "");

export default function Comunidade() {
  const [wa, setWa] = useState(null);

  useEffect(() => {
    fetchWhatsapp().then(setWa).catch(() => setWa(null));
  }, []);

  const appUrl = typeof window !== "undefined" ? window.location.origin : "";

  const handleInvite = async () => {
    const res = await shareContent({
      title: "Bíblia com Propósito · C E Medeiros",
      text: "Comece uma caminhada diária com Deus. Baixe o app.",
      url: appUrl,
    });
    if (res === "copied") toast.success("Convite copiado!");
    else if (res === "shared") toast.success("Obrigado por compartilhar!");
    else if (res === "failed") toast.error("Não foi possível compartilhar agora.");
  };

  const waNumber = normalizeWa(wa?.number);
  const waMsg = encodeURIComponent(
    wa?.message || "Olá, gostaria de enviar um pedido de oração."
  );
  const waHref = waNumber ? `https://wa.me/${waNumber}?text=${waMsg}` : null;

  return (
    <div className="max-w-3xl mx-auto section-padding py-8" data-testid="community-page">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy mb-6"
        data-testid="community-back"
      >
        <ArrowLeft className="w-4 h-4" /> Início
      </Link>

      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">Juntos na caminhada</p>
        <h1 className="font-display text-4xl sm:text-5xl text-navy mt-2">Comunidade</h1>
        <p className="text-navy/70 mt-3 text-sm max-w-xl">
          A vida cristã floresce em comunhão. Aqui você encontra canais simples para
          convidar amigos, acompanhar o pastor Carlos e compartilhar pedidos de oração.
        </p>
      </div>

      <ul className="space-y-3">
        {/* Invite friends */}
        <li>
          <button
            type="button"
            onClick={handleInvite}
            data-testid="community-invite"
            className="group w-full flex items-center gap-4 bg-white border border-border rounded-2xl p-5 card-lift text-left"
          >
            <Icon><UserPlus className="w-5 h-5 text-gold" /></Icon>
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-lg text-navy">Convide um amigo</h3>
              <p className="text-sm text-navy/60 mt-0.5">
                Envie o app para alguém que precisa de uma palavra hoje.
              </p>
            </div>
            <Share2 className="w-4 h-4 text-navy/40 group-hover:text-gold transition" />
          </button>
        </li>

        {/* WhatsApp prayer request */}
        {waHref && (
          <li>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="community-whatsapp"
              className="group flex items-center gap-4 bg-white border border-border rounded-2xl p-5 card-lift"
            >
              <Icon><MessageCircle className="w-5 h-5 text-gold" /></Icon>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-lg text-navy">Enviar pedido de oração</h3>
                <p className="text-sm text-navy/60 mt-0.5">
                  Escreva direto pelo WhatsApp. Sua palavra será recebida com carinho.
                </p>
              </div>
              <ExternalLink className="w-4 h-4 text-navy/40 group-hover:text-gold transition" />
            </a>
          </li>
        )}

        {/* Local prayer form (existing /oracao) */}
        <li>
          <Link
            to="/oracao"
            data-testid="community-prayer-form"
            className="group flex items-center gap-4 bg-white border border-border rounded-2xl p-5 card-lift"
          >
            <Icon><Heart className="w-5 h-5 text-gold" /></Icon>
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-lg text-navy">Pedido de oração no app</h3>
              <p className="text-sm text-navy/60 mt-0.5">
                Um espaço silencioso para escrever o que seu coração precisa.
              </p>
            </div>
            <ExternalLink className="w-4 h-4 text-navy/40 group-hover:text-gold transition" />
          </Link>
        </li>

        {SOCIAL_LINKS.instagram && (
          <li>
            <a
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="community-instagram"
              className="group flex items-center gap-4 bg-white border border-border rounded-2xl p-5 card-lift"
            >
              <Icon><Instagram className="w-5 h-5 text-gold" /></Icon>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-lg text-navy">Siga no Instagram</h3>
                <p className="text-sm text-navy/60 mt-0.5">@ccarlosmedeiross · reflexões e reels diários</p>
              </div>
              <ExternalLink className="w-4 h-4 text-navy/40 group-hover:text-gold transition" />
            </a>
          </li>
        )}

        {SOCIAL_LINKS.facebook && (
          <li>
            <a
              href={SOCIAL_LINKS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="community-facebook"
              className="group flex items-center gap-4 bg-white border border-border rounded-2xl p-5 card-lift"
            >
              <Icon><Facebook className="w-5 h-5 text-gold" /></Icon>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-lg text-navy">Siga no Facebook</h3>
                <p className="text-sm text-navy/60 mt-0.5">Conteúdo adicional e transmissões.</p>
              </div>
              <ExternalLink className="w-4 h-4 text-navy/40 group-hover:text-gold transition" />
            </a>
          </li>
        )}
      </ul>

      <p className="text-xs text-navy/50 mt-10">
        Em breve: espaço para testemunhos aprovados pelo pastor e mural de oração anônimo.
      </p>
    </div>
  );
}

function Icon({ children }) {
  return (
    <div className="w-11 h-11 rounded-xl bg-[hsl(var(--parchment))] flex items-center justify-center shrink-0">
      {children}
    </div>
  );
}
